import { Controller } from "@hotwired/stimulus"
import { loadPlan, savePlan } from "plan_storage"
import { getUserPref, setUserPref } from "user_prefs"
import { findOpenQuestions, composeOpenQEmail, fillNameTokens } from "tbd_questions"
import { renderIcons } from "lucide_icons"

// Open Questions section (editor-only; never in the print preview).
// The plan is scanned ON DEMAND — when the section is opened or the user
// clicks Rescan — NOT on every keystroke. This is the deliberate move
// away from the live per-edit whole-plan scan that slowed the editor.
//
// "Email OpenQ" composes a mailto from a per-user template (persisted in
// /api/preferences → openq_email_subject / openq_email_body) with the
// scanned questions injected.
const DEFAULT_SUBJECT = "A few questions about {{child}}'s plan"
const DEFAULT_BODY =
  "Hi,\n\n" +
  "While putting together {{child}}'s person-centered plan, we ran into a few " +
  "details we couldn't fill in. Could you help with the questions below? Just type " +
  "your answer after each one and reply — thank you!\n\n" +
  "{{questions}}\n\n" +
  "Thank you!"

export default class extends Controller {
  static targets = ["list", "count", "panel", "subject", "body", "status", "emailBtn", "includeBtn", "includeLabel"]

  connect() {
    // Hydrate the template editor from the saved per-user prefs (the
    // meta tag already backfills the built-in default).
    if (this.hasSubjectTarget) this.subjectTarget.value = getUserPref("openq_email_subject", DEFAULT_SUBJECT)
    if (this.hasBodyTarget)    this.bodyTarget.value    = getUserPref("openq_email_body", DEFAULT_BODY)

    // Rescan whenever this section becomes the active one again (the
    // editor fires this on every section switch). connect() itself
    // covers the first open / deep-link case.
    this._onSectionChange = (e) => {
      if (e.detail && e.detail.sectionId === "openQuestions") this.scan()
    }
    document.addEventListener("editor-native:section-change", this._onSectionChange)
    // Keep the "Include in Review" toggle in sync if it's flipped elsewhere
    // (e.g. the toolbar toggle) — both write plan.meta.includeOpenQuestions.
    this._onPlanChange = (e) => {
      if (e.detail && e.detail.path === "meta.includeOpenQuestions") this._reflectInclude(this._plan())
    }
    document.addEventListener("plan:change", this._onPlanChange)
    this.scan()
  }

  disconnect() {
    document.removeEventListener("editor-native:section-change", this._onSectionChange)
    document.removeEventListener("plan:change", this._onPlanChange)
    clearTimeout(this._statusTimer)
  }

  // Include / exclude the consolidated Open Questions page in the Review + PDF —
  // the same plan.meta.includeOpenQuestions flag the toolbar toggle uses.
  toggleInclude(event) {
    event?.preventDefault?.()
    const plan = this._plan()
    if (!plan.meta) plan.meta = {}
    const next = !plan.meta.includeOpenQuestions
    plan.meta.includeOpenQuestions = next
    savePlan({ meta: { includeOpenQuestions: next } })
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "meta.includeOpenQuestions", value: next, plan }
    }))
    this._reflectInclude(plan)
  }

  _reflectInclude(plan) {
    if (!this.hasIncludeBtnTarget) return
    const on = !!(plan && plan.meta && plan.meta.includeOpenQuestions)
    this.includeBtnTarget.classList.toggle("is-on", on)
    this.includeBtnTarget.setAttribute("aria-pressed", on ? "true" : "false")
    this.includeBtnTarget.title = `Open Questions in Review — ${on ? "ON" : "OFF"}`
    const icon = this.includeBtnTarget.querySelector("i, svg")
    if (icon) { icon.setAttribute("data-lucide", on ? "eye" : "eye-off"); renderIcons(this.includeBtnTarget) }
    if (this.hasIncludeLabelTarget) this.includeLabelTarget.textContent = on ? "In Review" : "Not in Review"
  }

  _plan() {
    try { return loadPlan() || {} } catch (_) { return {} }
  }

  scan() {
    const plan = this._plan()
    this._items = findOpenQuestions(plan)
    this._reflectInclude(plan)
    this._render(plan)
  }

  rescan() { this.scan() }

  _render(plan) {
    const items = this._items || []
    if (this.hasCountTarget) {
      this.countTarget.textContent = items.length
        ? `${items.length} open item${items.length === 1 ? "" : "s"}`
        : ""
    }
    if (this.hasEmailBtnTarget) this.emailBtnTarget.disabled = items.length === 0
    if (!this.hasListTarget) return
    if (!items.length) {
      this.listTarget.innerHTML =
        `<div class="oq-empty"><i data-lucide="check-circle"></i><p>No open questions — every field has a value.</p></div>`
      renderIcons(this.listTarget)
      return
    }
    this.listTarget.innerHTML = items.map((it, i) => {
      const q   = this._esc(fillNameTokens(it.question, plan))
      const val = this._esc(String(it.value || ""))
      const sid = this._esc(it.sectionId || "")
      return `
        <div class="oq-card">
          <div class="oq-q">
            <span class="oq-qnum">Q${i + 1}</span>
            <span class="oq-qtext">${q}</span>
            ${sid ? `<button type="button" class="oq-open" data-action="open-questions#jumpTo" data-section="${sid}" data-value="${val}">Open &rarr;</button>` : ""}
          </div>
          <div class="oq-current">Current: <span class="oq-current-pill">${val}</span></div>
        </div>`
    }).join("")
    renderIcons(this.listTarget)
  }

  // Jump to the EXACT field that holds this question: switch to its section
  // (reusing the sidebar nav button — same setActive path a real click takes),
  // then scroll to the field, focus it, select the TBD text, and flash it.
  jumpTo(event) {
    const btn = event.currentTarget
    const sid = btn.dataset.section
    if (!sid) return
    const value = btn.dataset.value || ""
    const link = document.querySelector(`.en-sidebar-link[data-section-key="${sid}"]`)
    if (link) link.click()
    const name = (this._plan().client?.firstName || "").trim()
    this._focusTbdField(sid, value, name, 0)
  }

  // Poll (the section lazy-mounts) until the field appears, then reveal it.
  _focusTbdField(sid, value, name, tries) {
    const section = document.querySelector(`[data-section="${sid}"]:not([hidden])`)
    const field = section ? this._findTbdField(section, value, name) : null
    if (!field) {
      if (tries < 45) requestAnimationFrame(() => this._focusTbdField(sid, value, name, tries + 1))
      return
    }
    field.classList.add("pcp-tbd-field")
    field.scrollIntoView({ behavior: "smooth", block: "center" })
    // Restart the flash animation even if the class is already present.
    field.classList.remove("pcp-tbd-jump")
    void field.offsetWidth
    field.classList.add("pcp-tbd-jump")
    setTimeout(() => field.classList.remove("pcp-tbd-jump"), 2200)
    try {
      field.focus({ preventScroll: true })
      const span = this._tbdSpan(field.value || "")
      if (span && typeof field.setSelectionRange === "function") field.setSelectionRange(span[0], span[1])
    } catch (_) { /* focus/select unsupported on this field type */ }
  }

  // Among the section's TBD-bearing fields, pick the one matching this question.
  // Values shown in the editor may have [FirstName] resolved to the real name,
  // so compare on a normalized form (tokens + name stripped) to still match.
  _findTbdField(section, value, name) {
    const inputs = Array.from(section.querySelectorAll("input, textarea"))
      .filter(el => el.type !== "hidden" && el.type !== "password")
    const withTbd = inputs.filter(el => /\bTBD\b|\?\?/i.test(el.value || ""))
    if (withTbd.length <= 1) return withTbd[0] || null
    const target = this._norm(value, name)
    let best = withTbd.find(el => this._norm(el.value, name) === target)
    if (best) return best
    const part = this._norm(this._tbdPortion(value), name)
    best = part && withTbd.find(el => this._norm(el.value, name).includes(part))
    return best || withTbd[0]
  }

  _norm(s, name) {
    let t = String(s || "").toLowerCase()
      .replace(/\[\s*first[\s_]*name\s*\]|\{\{?\s*firstname\s*\}?\}/gi, " ")
    if (name) t = t.split(name.toLowerCase()).join(" ")
    return t.replace(/\s+/g, " ").trim()
  }

  // The TBD portion of a value: from the first TBD/?? marker to the end.
  _tbdPortion(value) {
    const m = String(value || "").match(/\bTBD\b|\?\?/i)
    return m ? String(value).slice(m.index) : ""
  }

  // Character span [start, end) of the TBD text inside a value, so we can
  // select exactly it — including a leading "(" and up to a trailing ")".
  _tbdSpan(value) {
    const v = String(value || "")
    const m = v.match(/\bTBD\b|\?\?/i)
    if (!m) return null
    let start = m.index
    if (start > 0 && v[start - 1] === "(") start -= 1
    const close = v.indexOf(")", m.index)
    const end = close >= 0 ? close + 1 : v.length
    return [start, end]
  }

  // Compose the email from the current template fields + a fresh scan, then
  // hand it to the user. mailto: alone is fragile — it silently does NOTHING
  // when no desktop mail client is registered (most people use Gmail/Outlook on
  // the web) and OSes drop the body past a length cap. So we ALWAYS copy the
  // full email to the clipboard first (guaranteed to work), then still try to
  // pop the desktop mail app for those who have one.
  async emailOpenQ() {
    const plan  = this._plan()
    const items = findOpenQuestions(plan)
    if (!items.length) { this._flash("No open questions to email yet."); return }
    const tpl = {
      subject: this.hasSubjectTarget ? this.subjectTarget.value : getUserPref("openq_email_subject", DEFAULT_SUBJECT),
      body:    this.hasBodyTarget    ? this.bodyTarget.value    : getUserPref("openq_email_body", DEFAULT_BODY)
    }
    const { subject, body } = composeOpenQEmail(tpl, plan, items)

    // 1) Clipboard fallback — the reliable path. Works on http://localhost and
    //    any https origin (both secure contexts).
    const copied = await this._copyToClipboard(`Subject: ${subject}\n\n${body}`)

    // 2) Best-effort desktop mail-app handoff via a real anchor click (more
    //    reliable across browsers than assigning window.location.href). Long
    //    bodies are dropped by the OS, so past ~1500 chars we send a short
    //    prompt and let the clipboard carry the full list.
    const mailtoBody = body.length > 1500
      ? "The full list of open questions was copied to your clipboard — paste it below."
      : body
    const a = document.createElement("a")
    a.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(mailtoBody)}`
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    setTimeout(() => a.remove(), 0)

    this._flash(copied
      ? "Email copied to clipboard — opening your mail app; if nothing opens, paste it into Gmail/Outlook."
      : "Opening your mail app…")
  }

  // Copy text to the clipboard; returns true on success. Uses the async
  // Clipboard API where available, falling back to a hidden-textarea execCommand
  // for older browsers / non-secure contexts.
  async _copyToClipboard(text) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
        return true
      }
    } catch (_) { /* fall through to the legacy path */ }
    try {
      const ta = document.createElement("textarea")
      ta.value = text
      ta.setAttribute("readonly", "")
      ta.style.position = "fixed"
      ta.style.top = "-1000px"
      ta.style.opacity = "0"
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand("copy")
      ta.remove()
      return ok
    } catch (_) { return false }
  }

  toggleTemplate() {
    if (this.hasPanelTarget) this.panelTarget.hidden = !this.panelTarget.hidden
  }

  async saveTemplate() {
    const subject = this.hasSubjectTarget ? this.subjectTarget.value.trim() : ""
    const body    = this.hasBodyTarget    ? this.bodyTarget.value           : ""
    await setUserPref("openq_email_subject", subject || DEFAULT_SUBJECT)
    await setUserPref("openq_email_body", (body && body.trim()) ? body : DEFAULT_BODY)
    this._flash("Saved — used for every Email OpenQ")
  }

  resetTemplate() {
    if (this.hasSubjectTarget) this.subjectTarget.value = DEFAULT_SUBJECT
    if (this.hasBodyTarget)    this.bodyTarget.value    = DEFAULT_BODY
    this._flash("Reset — click Save template to keep it")
  }

  _flash(msg) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = msg
    clearTimeout(this._statusTimer)
    this._statusTimer = setTimeout(() => {
      if (this.hasStatusTarget) this.statusTarget.textContent = ""
    }, 2600)
  }

  _esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, c => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }
}
